import { ApiError, retryAfterSeconds } from "./client";
import type { ToastTone } from "@/components/ui/toastStore";

/**
 * Turning a failed mutation into something a person can read (backlog item 11).
 *
 * Kept apart from the wiring in `main.tsx` so the rule is one readable list
 * rather than a branch inside a callback: *what* a failure says is a product
 * decision, *where* it is announced is plumbing.
 */

/**
 * The opt-out, typed.
 *
 * Declared here rather than left as the default `Record<string, unknown>`, so
 * that a misspelled key or a wrong value is a compile error rather than a
 * mutation that quietly reports its failure twice — the same class of silent
 * mistake this item exists to remove.
 */
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * `"inline"` — this mutation's failures are rendered by the surface that
       * calls it, and the global toast stands down. Anything else, including
       * omitting it, is announced.
       */
      errorSurface?: "inline";
    };
  }
}

export interface FailureNotice {
  title: string;
  message: string;
  code?: string;
  tone: ToastTone;
  urgent: boolean;
}

/**
 * Every refusal in this API is already written for a person to read — the
 * guard's reason, the health check's verdict, `prompt_invalid`'s summary. So
 * the message is *always* the server's; a house sentence over the top of one
 * that says exactly what was wrong is a downgrade.
 *
 * `detail` is deliberately not rendered. It is structured for some routes and
 * absent for most (`prompt_invalid` ships `{problems: [...]}`), and a generic
 * surface that guessed at its shape would be wrong more often than useful —
 * the pages that know the shape render it themselves and opt out of this
 * entirely.
 */
export function describeFailure(error: unknown): FailureNotice {
  if (!(error instanceof ApiError)) {
    return {
      title: "The server could not be reached",
      message:
        "The request did not complete. Check your connection and try again — nothing was changed.",
      tone: "error",
      urgent: false,
    };
  }

  // An expired session is not a failed action, it is a locked deployment. It
  // says so itself, and it deserves to interrupt: everything the reader does
  // next will fail the same way until they unlock again.
  if (error.status === 401) {
    return {
      title: "Your session has expired",
      message: `${error.message} Reload this page to unlock again — anything unsaved here is still on screen.`,
      code: error.code,
      tone: "warning",
      urgent: true,
    };
  }

  const retry = retryAfterSeconds(error);
  if (retry !== null) {
    return {
      title: "Too many requests",
      message: `${error.message} Try again in ${Math.ceil(retry)}s.`,
      code: error.code,
      tone: "warning",
      urgent: false,
    };
  }

  // Everything else is `polite`. `urgent` means one thing and keeps meaning
  // it: what you do next will fail the same way. A refused action is bad news
  // about that action; interrupting a screen reader mid-sentence for each one
  // would make the urgent case indistinguishable from the ordinary one.
  return {
    title: TITLES[error.code] ?? "That did not go through",
    message: error.message,
    code: error.code,
    tone: "error",
    urgent: false,
  };
}

/**
 * A short label for the codes where "that did not go through" undersells what
 * happened. Anything absent falls back — a title is a signpost, and inventing
 * one per code would rot the moment the backend adds another.
 */
const TITLES: Record<string, string> = {
  sql_rejected: "Blocked by the read-only guard",
  forbidden: "Not permitted",
  not_found: "No longer there",
  conflict: "That conflicts with the current state",
  unprocessable_entity: "That could not be accepted",
  unavailable: "Temporarily unavailable",
  internal_error: "The server failed",
};
